'use client';

import { useRef, useState } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';

export default function PricingSection({ isLoggedIn = false }: { isLoggedIn?: boolean; currentPlan?: string }) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [playing, setPlaying] = useState(false);

  function toggleFounderMessage() {
    if (!audioRef.current) {
      audioRef.current = new Audio('/audio/FromMark.m4a');
      audioRef.current.addEventListener('ended', () => setPlaying(false));
    }
    if (playing) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setPlaying(false);
    } else {
      audioRef.current.play();
      setPlaying(true);
    }
  }

  return (
    <section className="relative py-20 px-6 bg-gradient-to-b from-background to-muted/40 overflow-hidden">
      {/* Mountain background at 30% opacity */}
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: "url('/images/mountain-range.jpg')", opacity: 0.3 }}
        aria-hidden="true"
      />
      <div className="relative max-w-3xl mx-auto">

        {/* Heading */}
        <div className="text-center mb-14 animate-fade-in-up">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 rounded-full mb-6">
            <Icon name="HeartIcon" size={16} className="text-primary" />
            <span className="text-sm font-semibold text-primary">Always Free</span>
          </div>
          <h2 className="font-jakarta font-bold text-4xl md:text-5xl text-foreground mb-4 leading-tight">
            Why Free?
          </h2>
          <button
            onClick={toggleFounderMessage}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-card border border-border rounded-xl text-sm font-semibold text-foreground hover:border-primary hover:bg-primary/5 transition-all duration-200 shadow-sm"
          >
            <Icon name={playing ? 'StopCircleIcon' : 'PencilSquareIcon'} size={16} className="text-primary" />
            {playing ? 'Stop' : 'Message from our Founder'}
          </button>
        </div>

        {/* Text content */}
        <div className="bg-card border border-border rounded-3xl p-10 shadow-sm animate-fade-in-up delay-100 space-y-6 text-foreground/80 leading-relaxed text-lg">
          <p>
            Legacy.bm was created as a space for you to write your own personal story, in your own voice. Each of us carries a lifetime of experiences, lessons, memories, and moments that shape who we are, yet so little of that truly gets written down. This site is an open invitation to tell people a little more about yourself — who you are, what matters to you, and the journey that brought you here. By sharing your story, you&apos;re keeping a piece of your history safe and accessible for those who may one day want to understand you better.
          </p>
          <p>
            Over time, life inevitably brings moments of change, distance, and loss, and it is often then that stories become most meaningful. Legacy.bm is free because everyone deserves a place to preserve their story and leave words behind for the people they love dearly. Whether you choose to share publicly or keep messages private for family, friends, or future generations, this space exists to hold those memories with respect and care. It&apos;s not just about remembering — it&apos;s about connection, love, and ensuring that what you&apos;ve lived and felt is never truly lost.
          </p>
        </div>

        {/* CTA */}
        <div className="text-center mt-12 animate-fade-in-up delay-200">
          <Link
            href={isLoggedIn ? '/dashboard' : '/signup'}
            className="inline-flex items-center gap-2 px-8 py-4 bg-primary text-primary-foreground rounded-2xl font-semibold text-base hover:bg-primary/90 transition-all duration-200 hover:scale-[1.02] active:scale-[0.98] shadow-lg shadow-primary/20"
          >
            {isLoggedIn ? 'Go to Dashboard' : 'Get Started Free'}
            <Icon name="ArrowRightIcon" size={18} />
          </Link>
          <p className="text-sm text-muted-foreground mt-4">
            No credit card required. No subscription. Ever.
          </p>
        </div>

      </div>
    </section>
  );
}
